import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './FinancialGuruArticlePage.module.css';
import LivesDisplay from '../components/LivesDisplay';
import { useQuest } from '../hooks/useQuest';
import { useLives } from '../context/LivesContext';
import { addXP } from '../utils/questUtils';

const FinancialGuruArticlePage: React.FC = () => {
  const navigate = useNavigate();
  const financialGuruQuest = useQuest('financial-guru');
  const { addLives } = useLives();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleBack = () => {
    console.log('[FinancialGuruArticlePage] Back button clicked, completing quest');
    financialGuruQuest.completeQuest(); // Mark as complete
    addLives(5); // Add 5 lives for completing the financial guru quest
    addXP(50); // Award 50 XP for completing a quest
    console.log('[FinancialGuruArticlePage] Quest completed, navigating back');
    navigate('/quests');
  };

  return (
    <div className={styles.articlePage}>
      <h1 className={styles.title}>The 50/30/20 Budgeting Method</h1>
      <LivesDisplay />
      <div className={styles.articleContent}>
        <p>Managing your money doesn't have to be complicated. The 50/30/20 budgeting method is a straightforward, effective approach that simplifies personal finance. Here's how it works:</p>
        <h2>What is the 50/30/20 Budgeting Method?</h2>
        <p>The 50/30/20 rule divides your after-tax income into three easy categories:</p>
        <ul>
          <li><b>50% Needs:</b> Essential expenses like rent or mortgage, groceries, utilities, insurance, transportation, and debt payments.</li>
          <li><b>30% Wants:</b> Non-essential items and activities you enjoy, such as dining out, entertainment, subscriptions, hobbies, and shopping.</li>
          <li><b>20% Savings:</b> Building financial security by saving money, investing, or paying down additional debt.</li>
        </ul>
        <h2>How to Start Using the 50/30/20 Method</h2>
        <ol>
          <li><b>Calculate Your After-Tax Income:</b> Determine how much money you have coming in each month after taxes.</li>
          <li><b>Break It Down:</b> Split your income based on the 50/30/20 percentages. For example, if your monthly after-tax income is $3,000:
            <ul>
              <li>Needs: $1,500 (50%)</li>
              <li>Wants: $900 (30%)</li>
              <li>Savings: $600 (20%)</li>
            </ul>
          </li>
          <li><b>Categorize Your Spending:</b> Review your expenses from the previous month and assign them to one of the three categories.</li>
          <li><b>Adjust as Necessary:</b> If your spending doesn't align with the recommended percentages, identify areas where you can cut back or reallocate funds.</li>
        </ol>
        <h2>Benefits of the 50/30/20 Method</h2>
        <ul>
          <li><b>Simplicity:</b> Easy to understand and implement.</li>
          <li><b>Balance:</b> Encourages balanced spending habits, allowing for enjoyment while prioritizing savings and essentials.</li>
          <li><b>Flexibility:</b> Easy to adjust and customize based on your financial situation and goals.</li>
        </ul>
        <h2>Tips for Success</h2>
        <ul>
          <li>Regularly track and review your spending to stay aligned with the percentages.</li>
          <li>Automate your savings contributions for consistency.</li>
          <li>Remember, the percentages are guidelines—adjust them slightly if needed to fit your personal circumstances.</li>
        </ul>
        <p>By following the 50/30/20 method, you'll find budgeting easier, gain greater financial clarity, and move steadily toward your financial goals.</p>
      </div>
      <button className={styles.backButton} onClick={handleBack}>Back to Quests</button>
    </div>
  );
};

export default FinancialGuruArticlePage; 